import { TravelRequestsListApi, TravelRequestsQueryFilter, TravelRequestsQueryRecord, TravelRequestsQueryType } from 'api/travel-requests-list';
import { autoinject } from 'aurelia-framework';
import { RouteConfig, Router } from 'aurelia-router';
import { watch } from 'aurelia-watch-decorator';
import { tryExecute } from 'framework/error_handling/error-utils';
import { log } from 'framework/script_utilities/dev-decorators';
import { TaskCounter } from 'framework/script_utilities/task-executor';
import { TextHighlightValueConverter } from 'framework/value_converters/text-highlight';
import { ApprovalStatus } from 'model/business-enums';
import { QueryParams } from 'model/essential-types';
import { EnumsService } from 'services/enums-service';
import { requireServices } from 'support/activation';
import * as E from 'support/errors';
import { BATCH_SIZE, HOME_ROUTE, INLINE_SEPARATOR } from 'support/settings';
import { QueryMemory } from 'support/shared_models/query-memory';

@autoinject
export class TravelRequestsCommonList {
  queryType: TravelRequestsQueryType = null;
  requests: TravelRequestsQueryRecord[] = null;
  lastQueryId = 0;
  loaded = false;

  constructor(private enums: EnumsService, private api: TravelRequestsListApi, private router: Router, public queryMemory: QueryMemory<TravelRequestsQueryFilter>, private taskCounter: TaskCounter) { }

  async canActivate() {
    await requireServices(this.enums);
  }

  @log('debug')
  activate(_params, route: RouteConfig) {
    this.queryType = route.settings.queryType as TravelRequestsQueryType;
  }

  bind() {
    this.queryRequests();
  }

  filterMutated() {
    this.taskCounter.execute(() => tryExecute(() => this.queryRequests(), E.FILTER_ERROR_MESSAGE, E.FILTER_ERROR_TITLE, true));
  }

  @watch((x: TravelRequestsCommonList) => x.queryMemory.sortExpression)
  sortExpressionChanged() {
    this.taskCounter.execute(() => tryExecute(() => this.queryRequests(), E.SORT_ERROR_MESSAGE, E.SORT_ERROR_TITLE, true));
  }

  async queryRequests() {
    const queryId = ++this.lastQueryId;
    const requests = await this.fetchRequests(0, this.batchSize);
    if (queryId === this.lastQueryId) {
      this.requests = requests;
      this.loaded = true;
    }
  }

  async fetchRequests(skip: number, take: number) {
    const highlight = this.queryMemory.filter.text;
    const params: QueryParams = { sortExpression: this.queryMemory.sortExpression, skip: skip, take: take };
    const requests = await this.api.queryTravelRequests(this.queryType, this.queryMemory.filter, params);
    return requests.map(r => this.extendRequest(r, highlight));
  }

  private extendRequest(request: TravelRequestsQueryRecord, expression: string) {
    const converter = new TextHighlightValueConverter();
    const highlight = (line: string) => converter.toView(line, expression)?.map(s => s.highlight ? `<span class="--highlight">${s.substring}</span>` : s.substring).join('');
    request['highlightedName'] = highlight(request.name);
    request['travelersNames'] = request.travelers.map(t => highlight(t.name)).join(', ');
    request['travelersInitials'] = request.travelers.map(t => t.initials).join(', ');
    request['travelersTitle'] = request.travelers.map(t => t.name).join('\n');
    request['countriesCombined'] = request.countries.map(c => (c.country.formFlag != null ? `<i class="${c.country.formFlag}"></i> ` : '') + highlight(c.country.name) + (c.status != null ? `: <span class="at-approval-status --${ApprovalStatus[c.status.value].toLowerCase()}">${c.status.name.toLowerCase()}</span>` : '')).join(INLINE_SEPARATOR);
    request['countriesTitle'] = request.countries.map(c => c.country.name + (c.status != null ? `: ${c.status.name.toLowerCase()}` : '')).join('\n');
    request['statusClass'] = `--${ApprovalStatus[request.status.value].toLowerCase()}`;
    request['statusDatePrefix'] = [ApprovalStatus.Approved, ApprovalStatus.Declined].includes(request.status.value) ? 'on' : 'since';
    return request;
  }

  get homeRoute() {
    return HOME_ROUTE;
  }

  get batchSize() {
    return BATCH_SIZE;
  }

  showRequest(request: TravelRequestsQueryRecord) {
    this.router.navigate(`#/${HOME_ROUTE}/requests/${request.id}`);
  }
}
